import {
  AddIcon,
  BagIcon,
  Button,
  Flex,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

const options = [
  {
    label: 'Full-width button',
    value: 'card_bottom_full',
    defaultOption: true,
  },
  {
    label: 'Icon next to price',
    value: 'card_bottom',
    iconSelection: true,
  },
  {
    label: 'Icon on image (bottom)',
    value: 'image_bottom',
    iconSelection: true,
  },
  {
    label: 'Icon on image (top)',
    value: 'image_top',
    iconSelection: true,
  },
];

export const ProductCardsCtaStyle = () => {
  const { setValue, watch } = useFormContext();

  const styleValue = watch('product_cards.cta_style');
  const iconValue = watch('product_cards.cta_icon');

  const handleOptionChange = (option: string | boolean, name?: string) => {
    setValue(name || 'product_cards.cta_style', option, { shouldDirty: true });
  };

  const radioOptions = useMemo(() => {
    return options.map(
      ({ value, label, defaultOption, iconSelection }, index) => {
        const showIconSelection = styleValue === value && iconSelection;
        return {
          id: value.toString(),
          value,
          label,
          wrapper: (children: ReactNode) => (
            <Flex
              mb={index === options.length - 1 ? 0 : 16}
              justifyContent="space-between"
              alignItems="center"
              border="grays-light"
              py={showIconSelection ? 12 : 16}
              px={16}
              ariaLabel={label}
              borderRadius="sm"
            >
              {children}
              {defaultOption && (
                <Tag label="Default" background="grays-ultralight" size="sm" />
              )}
              {showIconSelection && (
                <Flex>
                  <Button
                    variant={iconValue === 'add' ? 'secondary' : 'tertiary'}
                    size="small"
                    onClick={() =>
                      handleOptionChange('add', 'product_cards.cta_icon')
                    }
                    style={{
                      borderRadius:
                        'var(--border-radius-lg) 0px 0px var(--border-radius-lg)',
                      padding: '6px 12px',
                    }}
                    label={
                      <AddIcon
                        size="sm"
                        color={iconValue === 'add' ? 'primary' : 'grays-mid'}
                      />
                    }
                  />
                  <Button
                    variant={iconValue === 'bag' ? 'secondary' : 'tertiary'}
                    size="small"
                    onClick={() =>
                      handleOptionChange('bag', 'product_cards.cta_icon')
                    }
                    style={{
                      borderRadius:
                        '0px var(--border-radius-lg) var(--border-radius-lg) 0px',
                      padding: '6px 12px',
                    }}
                    label={
                      <BagIcon
                        size="sm"
                        color={iconValue === 'bag' ? 'primary' : 'grays-mid'}
                      />
                    }
                  />
                </Flex>
              )}
            </Flex>
          ),
        };
      }
    );
  }, [options, iconValue, styleValue]);

  return (
    <Flex flexDirection="column">
      <Typography variant="body-semibold">Button placement</Typography>
      <Typography variant="small-body-regular" color="grays-mid" mb={8}>
        Where the button appears on each card
      </Typography>
      <Form.RadioFieldGroup
        typography="mini"
        name="product_cards.cta_style"
        options={radioOptions}
      />
    </Flex>
  );
};
